import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { ArrowLeft } from 'lucide-react';

import AccountWorkspaceTabs from '@/components/account/AccountWorkspaceTabs';
import ActiveAccountPrompt from '@/components/account/ActiveAccountPrompt';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useNavigation } from '@/context/NavigationContext';
import { setActiveAccount } from '@/lib/accounts';
import { buildAccountRoute, parseAccountRoute } from '@/lib/account-routing';
import {
  formatCurrency,
  formatDate,
  getLegacyDate,
  getLegacyNumber,
  getLegacyString,
  getMyAccountBillingSummary,
  listMyAccountBillingHistory,
  submitMyAccountPayment,
  type LegacyRecord,
  type MyAccountPaymentRequest
} from '@/lib/my-account';

const emptyPayment: MyAccountPaymentRequest = {
  amount: 0,
  cardName: '',
  cardNumber: '',
  cardExpMonth: '',
  cardExpYear: '',
  cardCvv: '',
  billingAddress: '',
  billingPostalCode: ''
};

export default function MyAccountBillingPage() {
  const { path, navigate } = useNavigation();
  const accountId = useMemo(() => parseAccountRoute(path).accountId, [path]);

  useEffect(() => {
    if (!accountId) {
      return;
    }
    void setActiveAccount(accountId).catch(() => undefined);
  }, [accountId]);

  const { data: billingSummary, isLoading: billingLoading, error: billingError } = useSWR<LegacyRecord | null>(
    accountId ? ['/api/v1/my-account/billing/summary', accountId] : null,
    () => getMyAccountBillingSummary(accountId ?? undefined)
  );

  const { data: billingHistory = [], isLoading: historyLoading, error: historyError } = useSWR<LegacyRecord[]>(
    accountId ? ['/api/v1/my-account/billing/history', accountId, 36] : null,
    () => listMyAccountBillingHistory(accountId ?? undefined, 36)
  );

  const balances = useMemo(() => {
    if (!billingSummary) {
      return {
        total: null,
        current: null,
        past30: null,
        past60: null,
        past90: null
      };
    }

    return {
      total: getLegacyNumber(billingSummary, ['balance_amount', 'balanceamount', 'total_balance', 'balancet']),
      current: getLegacyNumber(billingSummary, ['current_balance', 'current', 'curr']),
      past30: getLegacyNumber(billingSummary, ['past_due_30', 'pastdue30', 'd30']),
      past60: getLegacyNumber(billingSummary, ['past_due_60', 'pastdue60', 'd60']),
      past90: getLegacyNumber(billingSummary, ['past_due_90', 'pastdue90', 'd90'])
    };
  }, [billingSummary]);

  const fullBalance = balances.total !== null ? Math.abs(balances.total) : null;

  const [payment, setPayment] = useState<MyAccountPaymentRequest>(emptyPayment);
  const [payFullBalance, setPayFullBalance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  if (!accountId) {
    return <ActiveAccountPrompt />;
  }

  const canSubmit =
    payment.amount > 0 &&
    payment.cardName.trim() &&
    payment.cardNumber.trim() &&
    payment.cardExpMonth.trim() &&
    payment.cardExpYear.trim() &&
    payment.cardCvv.trim() &&
    payment.billingAddress.trim() &&
    payment.billingPostalCode.trim();

  const handlePaymentChange = (field: keyof MyAccountPaymentRequest, value: string) => {
    setPayment(prev => ({
      ...prev,
      [field]: field === 'amount' ? (Number.isFinite(Number(value)) ? Number(value) : 0) : value
    }));
  };

  const handleToggleFullBalance = () => {
    setPayFullBalance(prev => {
      const next = !prev;
      if (next && typeof fullBalance === 'number') {
        setPayment(current => ({ ...current, amount: fullBalance }));
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      await submitMyAccountPayment({
        ...payment,
        amount: Number(payment.amount || 0)
      }, accountId ?? undefined);
      setSubmitSuccess('Payment submitted successfully.');
      setPayment(emptyPayment);
      setPayFullBalance(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit payment.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Button variant="ghost" type="button" onClick={() => navigate(buildAccountRoute(accountId))}>
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden />
          Back to overview
        </Button>
        <AccountWorkspaceTabs accountId={accountId} current="billing" />
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Billing</h1>
          <p className="text-sm text-muted-foreground">Review balances, payment history, and submit a card payment.</p>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Balance summary</CardTitle>
            <CardDescription>Legacy AR aging buckets.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm text-muted-foreground">
            {billingError ? <span className="text-destructive">{billingError.message}</span> : null}
            <BalanceRow label="Total balance" value={formatCurrency(balances.total)} loading={billingLoading} />
            <BalanceRow label="Current" value={formatCurrency(balances.current)} loading={billingLoading} />
            <BalanceRow label="Past due 30" value={formatCurrency(balances.past30)} loading={billingLoading} />
            <BalanceRow label="Past due 60" value={formatCurrency(balances.past60)} loading={billingLoading} />
            <BalanceRow label="Past due 90" value={formatCurrency(balances.past90)} loading={billingLoading} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Make a payment</CardTitle>
            <CardDescription>Payments are processed using the legacy stored card flow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submitSuccess ? <Badge variant="secondary">{submitSuccess}</Badge> : null}
            {submitError ? <div className="text-sm text-destructive">{submitError}</div> : null}

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  value={payment.amount || ''}
                  onChange={event => handlePaymentChange('amount', event.target.value)}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button type="button" variant="outline" onClick={handleToggleFullBalance}>
                  {payFullBalance ? 'Custom amount' : 'Pay full balance'}
                </Button>
                {payFullBalance && fullBalance !== null ? (
                  <span className="text-xs text-muted-foreground">{formatCurrency(fullBalance)}</span>
                ) : null}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cardName">Name on card</Label>
                <Input
                  id="cardName"
                  value={payment.cardName}
                  onChange={event => handlePaymentChange('cardName', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cardNumber">Card number</Label>
                <Input
                  id="cardNumber"
                  value={payment.cardNumber}
                  onChange={event => handlePaymentChange('cardNumber', event.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="cardExpMonth">Exp month</Label>
                <Input
                  id="cardExpMonth"
                  value={payment.cardExpMonth}
                  onChange={event => handlePaymentChange('cardExpMonth', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cardExpYear">Exp year</Label>
                <Input
                  id="cardExpYear"
                  value={payment.cardExpYear}
                  onChange={event => handlePaymentChange('cardExpYear', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cardCvv">CVV</Label>
                <Input
                  id="cardCvv"
                  value={payment.cardCvv}
                  onChange={event => handlePaymentChange('cardCvv', event.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="billingAddress">Billing address</Label>
                <Input
                  id="billingAddress"
                  value={payment.billingAddress}
                  onChange={event => handlePaymentChange('billingAddress', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billingPostalCode">Postal code</Label>
                <Input
                  id="billingPostalCode"
                  value={payment.billingPostalCode}
                  onChange={event => handlePaymentChange('billingPostalCode', event.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transactionNote">Transaction note (optional)</Label>
              <Input
                id="transactionNote"
                value={payment.transactionNote ?? ''}
                onChange={event => handlePaymentChange('transactionNote', event.target.value)}
              />
            </div>

            <Button type="button" onClick={handleSubmit} disabled={submitting || !canSubmit}>
              {submitting ? 'Submitting…' : 'Submit payment'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Payment history</CardTitle>
          <CardDescription>Latest transactions and service allocations.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {historyError ? <span className="text-destructive">{historyError.message}</span> : null}
          {historyLoading ? (
            <div className="text-sm text-muted-foreground">Loading payment history…</div>
          ) : billingHistory.length === 0 ? (
            <div className="text-sm text-muted-foreground">No payment history available.</div>
          ) : (
            billingHistory.map((record, index) => {
              const date = formatDate(getLegacyDate(record, ['transactiondate', 'transaction_date', 'paymentdate', 'date']));
              const amount = formatCurrency(getLegacyNumber(record, ['sum_amount', 'amount', 'payment_amount']));
              const method = getLegacyString(record, ['paymenttype', 'payment_method', 'method']) ?? 'Card';
              const service = getLegacyString(record, ['service', 'Service', 'service_description']);

              return (
                <div key={`payment-${index}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 p-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">{amount}</div>
                    <div className="text-xs text-muted-foreground">{date}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{service ?? 'Legacy transaction'}</div>
                  <Badge variant="outline">{method}</Badge>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BalanceRow({ label, value, loading }: { label: string; value: string; loading: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span>{label}</span>
      <span className="text-foreground">{loading ? '…' : value}</span>
    </div>
  );
}
